import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, FileText } from 'lucide-react';
import InterventionForm from './InterventionForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { en_attente: 'bg-amber-50 text-amber-600', en_cours: 'bg-blue-50 text-blue-600', termine: 'bg-green-50 text-green-600', annule: 'bg-gray-100 text-gray-500' };
const STATUS_LIST = ['en_attente', 'en_cours', 'termine', 'annule'];
const STATUS_LABELS = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Terminé', annule: 'Annulé' };

const emptyForm = {
  client_id: '', vehicle_id: '', description: '', status: 'en_attente',
  entry_date: new Date().toISOString().slice(0, 10), expected_date: '',
  technician: '', parts_used: [], time_spent: 0, estimated_amount: 0, final_amount: 0, notes: ''
};

export default function InterventionsModule({ authHeaders, refreshStats }) {
  const [interventions, setInterventions] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState('');
  const [form, setForm] = useState({ ...emptyForm });

  const fetchData = useCallback(async () => {
    const [iRes, cRes, vRes] = await Promise.all([
      axios.get(`${API_URL}/api/crm/interventions`, { headers: authHeaders, params: filter ? { status: filter } : {} }),
      axios.get(`${API_URL}/api/crm/clients`, { headers: authHeaders }),
      axios.get(`${API_URL}/api/crm/client-vehicles`, { headers: authHeaders })
    ]);
    setInterventions(iRes.data);
    setClients(cRes.data);
    setVehicles(vRes.data);
  }, [filter, authHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) await axios.put(`${API_URL}/api/crm/interventions/${editing.id}`, form, { headers: authHeaders });
      else await axios.post(`${API_URL}/api/crm/interventions`, form, { headers: authHeaders });
      setShowForm(false); setEditing(null); fetchData(); refreshStats?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette intervention ?')) return;
    await axios.delete(`${API_URL}/api/crm/interventions/${id}`, { headers: authHeaders });
    fetchData(); refreshStats?.();
  };

  const handleToQuote = async (id) => {
    if (!window.confirm('Générer un devis à partir de cette intervention ?')) return;
    try {
      await axios.post(`${API_URL}/api/crm/interventions/${id}/to-quote`, {}, { headers: authHeaders });
      alert('Devis créé avec succès !'); refreshStats?.();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const openEdit = async (id) => {
    const res = await axios.get(`${API_URL}/api/crm/interventions/${id}`, { headers: authHeaders });
    const it = res.data;
    setForm({
      client_id: it.client_id, vehicle_id: it.vehicle_id || '', description: it.description || '',
      status: it.status, entry_date: it.entry_date?.slice(0, 10) || '', expected_date: it.expected_date?.slice(0, 10) || '',
      technician: it.technician || '', parts_used: it.parts_used || [], time_spent: it.time_spent || 0,
      estimated_amount: it.estimated_amount || 0, final_amount: it.final_amount || 0, notes: it.notes || ''
    });
    setEditing(it); setShowForm(true);
  };

  return (
    <div data-testid="interventions-module">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-6">
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setFilter('')} className={`px-3 py-1.5 rounded-full text-xs font-medium ${!filter ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>Toutes</button>
          {STATUS_LIST.map(s => (
            <button key={s} onClick={() => setFilter(s)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === s ? 'bg-[#DC2626] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{STATUS_LABELS[s]}</button>
          ))}
        </div>
        <button onClick={() => { setForm({ ...emptyForm }); setEditing(null); setShowForm(true); }} className="flex items-center gap-2 bg-[#DC2626] text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700" data-testid="add-intervention-btn">
          <Plus size={16} /> Nouvelle intervention
        </button>
      </div>

      <div className="bg-white rounded-xl border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left p-3">Description</th>
                <th className="text-left p-3">Client</th>
                <th className="text-left p-3 hidden md:table-cell">Technicien</th>
                <th className="text-left p-3 hidden md:table-cell">Date entrée</th>
                <th className="text-right p-3 hidden lg:table-cell">Montant</th>
                <th className="text-center p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {interventions.map(it => (
                <tr key={it.id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium max-w-[200px] truncate">{it.description || '-'}</td>
                  <td className="p-3 text-gray-600">{it.client_name}</td>
                  <td className="p-3 hidden md:table-cell text-gray-600">{it.technician || '-'}</td>
                  <td className="p-3 hidden md:table-cell text-gray-500">{it.entry_date?.slice(0, 10)}</td>
                  <td className="p-3 text-right hidden lg:table-cell font-semibold">{(it.final_amount || it.estimated_amount || 0).toLocaleString('fr-FR')}€</td>
                  <td className="p-3 text-center">
                    <span className={`text-xs px-2 py-1 rounded-full ${STATUS_COLORS[it.status] || ''}`}>{STATUS_LABELS[it.status] || it.status}</span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(it.id)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Modifier"><Pencil size={15} /></button>
                      <button onClick={() => handleToQuote(it.id)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Créer un devis"><FileText size={15} /></button>
                      <button onClick={() => handleDelete(it.id)} className="p-1.5 hover:bg-red-50 rounded text-red-600" title="Supprimer"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!interventions.length && <tr><td colSpan={7} className="p-8 text-center text-gray-400">Aucune intervention</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <InterventionForm
          form={form} setForm={setForm}
          editing={editing} clients={clients} vehicles={vehicles}
          onSave={handleSave}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}
